import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download } from "lucide-react";

function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className="col-span-2 break-all font-mono text-xs">{children}</div>
    </div>
  );
}

function AuditDetailDialog({ entry, onOpenChange }) {
  const { t } = useI18n();
  return (
    <Dialog open={!!entry} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="audit-detail-dialog">
        <DialogHeader>
          <DialogTitle>{t("audit.detailTitle")}</DialogTitle>
        </DialogHeader>
        {entry && (
          <div className="max-h-[70vh] space-y-1 overflow-y-auto divide-y divide-border">
            <DetailRow label={t("audit.timestamp")}>
              {new Date(entry.timestamp).toLocaleString()}
            </DetailRow>
            <DetailRow label={t("audit.actor")}>
              {entry.actor_email} ({entry.actor_id})
            </DetailRow>
            <DetailRow label={t("audit.action")}>{entry.action}</DetailRow>
            <DetailRow label={t("audit.entityType")}>{entry.entity_type}</DetailRow>
            <DetailRow label={t("audit.entityId")}>{entry.entity_id}</DetailRow>
            <DetailRow label={t("audit.impersonating")}>
              {entry.impersonated_dealer_id || "—"}
            </DetailRow>
            <DetailRow label={t("audit.source")}>{entry.source}</DetailRow>
            <DetailRow label={t("audit.before")}>
              <pre className="whitespace-pre-wrap">
                {entry.before ? JSON.stringify(entry.before, null, 2) : "—"}
              </pre>
            </DetailRow>
            <DetailRow label={t("audit.after")}>
              <pre className="whitespace-pre-wrap">
                {entry.after ? JSON.stringify(entry.after, null, 2) : "—"}
              </pre>
            </DetailRow>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AuditExportDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setFrom("");
      setTo("");
    }
  }, [open]);

  const doExport = async () => {
    setBusy(true);
    try {
      const params = {};
      if (from) params.from_date = new Date(from).toISOString();
      if (to) params.to_date = new Date(to).toISOString();
      const { data } = await client.get("/audit-logs/export", {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit-log.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onOpenChange(false);
    } catch (e) {
      toast.error(t("audit.exportError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="audit-export-dialog">
        <DialogHeader>
          <DialogTitle>{t("audit.exportTitle")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t("audit.exportSubtitle")}</p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="audit-export-from">{t("audit.from")}</Label>
            <Input
              id="audit-export-from"
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="audit-export-from"
            />
          </div>
          <div>
            <Label htmlFor="audit-export-to">{t("audit.to")}</Label>
            <Input
              id="audit-export-to"
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="audit-export-to"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={doExport} disabled={busy} data-testid="audit-export-submit">
            {busy ? t("audit.exporting") : t("audit.exportAction")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AuditLog() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    client.get(`/audit-logs?limit=100`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-10" data-testid="audit-page">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <p className="data-label mb-2">AUDIT · LOG</p>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("audit.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("audit.subtitle")}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setExportOpen(true)}
          data-testid="audit-export-btn"
        >
          <Download className="mr-2 h-4 w-4" />
          {t("audit.export")}
        </Button>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="audit-table">
            <thead className="thead">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Actor</th>
                <th className="px-4 py-2 text-left">Action</th>
                <th className="px-4 py-2 text-left">Target</th>
                <th className="px-4 py-2 text-left">Impersonating</th>
              </tr>
            </thead>
            <tbody>
              {!items &&
                ["s1", "s2", "s3", "s4", "s5", "s6"].map((k) => (
                  <tr key={k} className="border-t border-border">
                    <td colSpan={5} className="px-4 py-3">
                      <Skeleton className="h-5 w-full" />
                    </td>
                  </tr>
                ))}
              {items &&
                items.map((a) => (
                  <tr
                    key={a.id}
                    className="row-hover cursor-pointer border-t border-border"
                    data-testid={`audit-row-${a.id}`}
                    onClick={() => setSelected(a)}
                  >
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs tabular-nums text-muted-foreground">
                      {new Date(a.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{a.actor_email}</td>
                    <td className="px-4 py-2 font-mono text-xs">{a.action}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {a.entity_type}/{a.entity_id?.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {a.impersonated_dealer_id ? a.impersonated_dealer_id.slice(0, 8) + "…" : "—"}
                    </td>
                  </tr>
                ))}
              {items && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center text-sm text-muted-foreground">
                    No audit entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AuditDetailDialog entry={selected} onOpenChange={(open) => !open && setSelected(null)} />
      <AuditExportDialog open={exportOpen} onOpenChange={setExportOpen} />
    </div>
  );
}
